<template>
  <div
    v-if="!message.hidden"
    class="message-item"
    :class="{
      'message-item--own': message.isOwn,
      'message-item--event': message.isEvent,
      'message-item--deleted': message.deleted,
      'message-item--pending': message.isPending,
      [`message-item--${platform}`]: platform,
    }"
  >
    <!-- Chat platforms: row with avatar + stack -->
    <div v-if="isChatPlatform" class="message-item__row">
      <div
        v-if="!message.isOwn"
        class="message-item__avatar"
        :style="{ backgroundColor: avatarColor }"
      >
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="metaDisplayName"
          class="message-item__avatar-img"
        />
        <span v-else class="message-item__avatar-initials">{{ avatarInitials }}</span>
      </div>
      <div v-else class="message-item__avatar message-item__avatar--own">
        <span class="message-item__avatar-initials">Me</span>
      </div>
      <div class="message-item__stack">
        <div
          class="message-item__bubble"
          :class="{
            'message-item__bubble--outgoing': message.isOwn,
            'message-item__bubble--incoming': !message.isOwn,
            [`message-item__bubble--${platform}`]: platform,
          }"
          :style="bubbleStyle"
        >
          <div v-if="showSender" class="message-item__meta">
            <span
              class="message-item__meta-name"
              :style="{ color: message.isOwn ? 'inherit' : avatarColor }"
              >{{ metaDisplayName }}</span
            >
            <span class="message-item__meta-sep">&middot;</span>
            <span class="message-item__meta-time">{{ formatChatTime(message.timestamp) }}</span>
          </div>
          <div v-if="message.subject" class="message-item__subject">{{ message.subject }}</div>
          <div v-if="message.isEvent" class="message-item__event">
            <Icon :icon="getEventIcon(message.eventType)" />
            <span>{{ getEventText(message.eventType) }}</span>
          </div>
          <div v-if="message.quoted && !message.deleted" class="message-item__quoted">
            <div class="message-item__quoted-header">
              <Icon icon="mdi:reply" />
              <span v-if="quotedSenderName" class="message-item__quoted-sender">
                {{ quotedSenderName }}
              </span>
            </div>
            <div v-if="message.quoted.text" class="message-item__quoted-text">
              {{ message.quoted.text }}
            </div>
            <div
              v-if="message.quoted.attachments && message.quoted.attachments.length > 0"
              class="message-item__quoted-attachments"
            >
              <AttachmentPreview
                v-for="attachment in message.quoted.attachments"
                :key="attachment.id"
                :attachment="attachment"
                :message-id="messageId"
                @image-click="handleImageClick"
              />
            </div>
          </div>
          <div v-if="message.deleted" class="message-item__body message-item__body--deleted">
            <Icon icon="mdi:delete-outline" />
            <span>Message deleted</span>
          </div>
          <div
            v-else-if="hasEmptyContent"
            class="message-item__body message-item__body--unsupported"
          >
            <Icon icon="mdi:alert-circle-outline" />
            <span
              >This message type isn't supported. Please check the original platform to view
              it.</span
            >
          </div>
          <div
            v-else
            class="message-item__body"
            :class="{ 'message-item__body--html': isHtmlContent }"
            v-html="formattedContent"
          ></div>
          <div
            v-if="message.attachments && message.attachments.length > 0"
            class="message-item__attachments"
          >
            <AttachmentPreview
              v-for="attachment in message.attachments"
              :key="attachment.id"
              :attachment="attachment"
              :message-id="messageId"
              @image-click="handleImageClick"
            />
          </div>
          <div
            v-if="message.reactions && message.reactions.length > 0"
            class="message-item__reactions"
          >
            <div
              v-for="(reaction, index) in message.reactions"
              :key="index"
              class="message-item__reaction"
              :class="{ 'message-item__reaction--own': reaction.is_sender }"
              :title="reaction.is_sender ? 'Click to remove your reaction' : undefined"
              @click="reaction.is_sender && $emit('remove-reaction')"
            >
              <span class="message-item__reaction-emoji">{{ reaction.value }}</span>
            </div>
          </div>
          <div
            v-if="message.message_type === 'INMAIL' || message.messageType === 'inmail'"
            class="message-item__type-badge"
          >
            <Icon icon="mdi:email" />
            <span>InMail</span>
          </div>
          <div v-if="message.attendee_type === 'ADMIN'" class="message-item__attendee-type-badge">
            <Icon icon="mdi:shield-account" />
            <span>Admin</span>
          </div>
          <div v-if="message.edited" class="message-item__edited">
            <Icon icon="mdi:pencil" />
            <span>Edited</span>
            <button
              v-if="message.original"
              class="message-item__show-original-btn"
              @click="showOriginal = !showOriginal"
            >
              {{ showOriginal ? "Hide" : "Show" }} original
            </button>
            <div v-if="showOriginal && message.original" class="message-item__original-content">
              {{ message.original }}
            </div>
          </div>
        </div>

        <div
          v-if="
            message.isOwn &&
            message.readReceipts &&
            message.readReceipts.length > 0 &&
            showReadReceipts
          "
          class="message-item__read-time message-item__read-time--inline"
        >
          <div
            v-if="platform === 'linkedin'"
            class="message-item__read-avatar"
            :style="{ backgroundColor: contactAvatarUrl ? 'transparent' : contactColor }"
          >
            <img
              v-if="contactAvatarUrl"
              :src="contactAvatarUrl"
              alt=""
              class="message-item__read-avatar-img"
            />
            <span v-else class="message-item__read-avatar-initials">{{ contactInitials }}</span>
          </div>
          <Icon
            v-else
            icon="mdi:check-circle"
            :style="{ color: '#10b981', fontSize: '0.875rem' }"
          />
          <span>Read {{ formatTimestamp(message.readReceipts[0].readAt) }}</span>
        </div>
        <div class="message-item__actions message-item__actions--hover">
          <button
            ref="reactButtonRef"
            class="message-item__action-icon-btn"
            type="button"
            title="React"
            @click="showReactionPicker = !showReactionPicker"
          >
            <Icon icon="mdi:emoticon-happy-outline" />
          </button>
          <button
            class="message-item__action-icon-btn"
            type="button"
            title="Reply"
            @click="$emit('reply')"
          >
            <Icon icon="material-symbols:reply-rounded" />
          </button>
          <button
            v-if="platform === 'whatsapp'"
            class="message-item__action-icon-btn"
            type="button"
            title="Forward"
            @click="$emit('forward')"
          >
            <Icon icon="material-symbols:forward-rounded" />
          </button>
          <button
            class="message-item__action-icon-btn"
            type="button"
            title="Delete"
            @click="$emit('delete')"
          >
            <Icon icon="mdi:delete-outline" />
          </button>
        </div>
        <MessageReactionPicker
          :show="showReactionPicker"
          :trigger-el="reactButtonRef"
          @select="onReactionSelect"
          @close="showReactionPicker = false"
        />
      </div>
    </div>

    <!-- Non-chat platforms: original stack layout -->
    <div v-else class="message-item__stack">
      <div
        class="message-item__bubble"
        :class="{
          'message-item__bubble--outgoing': message.isOwn,
          'message-item__bubble--incoming': !message.isOwn,
          [`message-item__bubble--${platform}`]: platform,
        }"
        :style="bubbleStyle"
      >
        <div v-if="!message.isOwn && showSender" class="message-item__sender">
          {{ message.senderName }}
        </div>
        <div v-if="message.subject" class="message-item__subject">{{ message.subject }}</div>
        <div v-if="message.isEvent" class="message-item__event">
          <Icon :icon="getEventIcon(message.eventType)" />
          <span>{{ getEventText(message.eventType) }}</span>
        </div>
        <div v-if="message.quoted && !message.deleted" class="message-item__quoted">
          <div class="message-item__quoted-header">
            <Icon icon="mdi:reply" />
            <span v-if="quotedSenderName" class="message-item__quoted-sender">
              {{ quotedSenderName }}
            </span>
          </div>
          <div v-if="message.quoted.text" class="message-item__quoted-text">
            {{ message.quoted.text }}
          </div>
          <div
            v-if="message.quoted.attachments && message.quoted.attachments.length > 0"
            class="message-item__quoted-attachments"
          >
            <AttachmentPreview
              v-for="attachment in message.quoted.attachments"
              :key="attachment.id"
              :attachment="attachment"
              :message-id="messageId"
              @image-click="handleImageClick"
            />
          </div>
        </div>
        <div v-if="message.deleted" class="message-item__body message-item__body--deleted">
          <Icon icon="mdi:delete-outline" />
          <span>Message deleted</span>
        </div>
        <div v-else-if="hasEmptyContent" class="message-item__body message-item__body--unsupported">
          <Icon icon="mdi:alert-circle-outline" />
          <span
            >This message type isn't supported. Please check the original platform to view it.</span
          >
        </div>
        <div
          v-else
          class="message-item__body"
          :class="{ 'message-item__body--html': isHtmlContent }"
          v-html="formattedContent"
        ></div>
        <div
          v-if="message.attachments && message.attachments.length > 0"
          class="message-item__attachments"
        >
          <AttachmentPreview
            v-for="attachment in message.attachments"
            :key="attachment.id"
            :attachment="attachment"
            :message-id="messageId"
            @image-click="handleImageClick"
          />
        </div>
        <div
          v-if="message.message_type === 'INMAIL' || message.messageType === 'inmail'"
          class="message-item__type-badge"
        >
          <Icon icon="mdi:email" />
          <span>InMail</span>
        </div>
        <div v-if="message.attendee_type === 'ADMIN'" class="message-item__attendee-type-badge">
          <Icon icon="mdi:shield-account" />
          <span>Admin</span>
        </div>
        <div
          v-if="message.reactions && message.reactions.length > 0"
          class="message-item__reactions"
        >
          <div
            v-for="(reaction, index) in message.reactions"
            :key="index"
            class="message-item__reaction"
          >
            <span class="message-item__reaction-emoji">{{ reaction.value }}</span>
          </div>
        </div>
        <div v-if="message.edited" class="message-item__edited">
          <Icon icon="mdi:pencil" />
          <span>Edited</span>
          <button
            v-if="message.original"
            class="message-item__show-original-btn"
            @click="showOriginal = !showOriginal"
          >
            {{ showOriginal ? "Hide" : "Show" }} original
          </button>
          <div v-if="showOriginal && message.original" class="message-item__original-content">
            {{ message.original }}
          </div>
        </div>
        <div class="message-item__footer">
          <span class="message-item__timestamp">{{
            formatMessageTimestamp(message.timestamp)
          }}</span>
          <span
            v-if="message.isOwn && (statusInfo.icon || message.isPending)"
            class="message-item__status"
          >
            <Icon
              v-if="message.isPending"
              icon="mdi:clock-outline"
              :style="{ color: '#9ca3af' }"
              title="Sending..."
            />
            <Icon
              v-else-if="statusInfo.icon"
              :icon="statusInfo.icon"
              :style="{ color: statusInfo.color }"
            />
          </span>
        </div>
      </div>

      <div class="message-item__actions">
        <button class="message-item__action-btn" @click="$emit('reply')">
          <Icon icon="mdi:reply" />
          Reply
        </button>
        <button class="message-item__action-btn" @click="$emit('ai-reply')">
          <Icon icon="mdi:sparkles" />
          AI Reply
        </button>
        <button class="message-item__action-btn" @click="$emit('forward')">
          <Icon icon="mdi:share-variant" />
          Forward
        </button>
        <button class="message-item__action-btn" @click="$emit('delete')">
          <Icon icon="mdi:delete-outline" />
        </button>
      </div>
    </div>

    <div
      v-if="
        !isChatPlatform &&
        message.isOwn &&
        message.readReceipts &&
        message.readReceipts.length > 0 &&
        showReadReceipts
      "
      class="message-item__read-time"
    >
      <div
        v-if="platform === 'linkedin'"
        class="message-item__read-avatar"
        :style="{ backgroundColor: contactAvatarUrl ? 'transparent' : contactColor }"
      >
        <img
          v-if="contactAvatarUrl"
          :src="contactAvatarUrl"
          alt=""
          class="message-item__read-avatar-img"
        />
        <span v-else class="message-item__read-avatar-initials">{{ contactInitials }}</span>
      </div>
      <Icon v-else icon="mdi:check-circle" :style="{ color: '#10b981', fontSize: '0.875rem' }" />
      <span>Read {{ formatTimestamp(message.readReceipts[0].readAt) }}</span>
    </div>
    <ImageLightbox
      :show="showImageLightbox"
      :images="lightboxImages"
      :current-index="lightboxCurrentIndex"
      @close="closeImageLightbox"
      @update:current-index="(index) => (lightboxCurrentIndex = index)"
    />
  </div>
</template>

<script setup lang="ts">
import type { Message, ChatParticipant } from "@/dtos/Unipile.dto";
import {
  formatMessageContent,
  getMaxMessageWidth,
  getPlatformColors,
  type Platform,
} from "@/utils/messageFormatters";
import { formatMessageTimestamp, getMessageStatus } from "@/utils/messageStatus";
import { Icon } from "@iconify/vue";
import { computed, ref } from "vue";
import AttachmentPreview from "./AttachmentPreview.vue";
import ImageLightbox from "./ImageLightbox.vue";
import MessageReactionPicker from "./MessageReactionPicker.vue";

interface Props {
  message: Message;
  platform?: Platform;
  showSender?: boolean;
  showReadReceipts?: boolean;
  participants?: ChatParticipant[];
}

const props = withDefaults(defineProps<Props>(), {
  platform: undefined,
  showSender: true,
  showReadReceipts: false,
});

const emit = defineEmits<{
  reply: [];
  "ai-reply": [];
  "ai-followup": [];
  forward: [];
  delete: [];
  react: [reaction: string];
  "remove-reaction": [];
}>();

const showOriginal = ref(false);
const showImageLightbox = ref(false);
const lightboxImages = ref<string[]>([]);
const lightboxCurrentIndex = ref(0);
const showReactionPicker = ref(false);
const reactButtonRef = ref<HTMLElement | null>(null);

const onReactionSelect = (emoji: string) => {
  emit("react", emoji);
  showReactionPicker.value = false;
};

const isChatPlatform = computed(() => {
  return props.platform === "whatsapp" || props.platform === "linkedin" || props.platform === "sms";
});

const metaDisplayName = computed(() => {
  if (props.message.isOwn) return "Me";
  return props.message.senderName || "Unnamed";
});

// Avatar helpers
const avatarUrl = computed(() => {
  if (props.message.isOwn) return null;
  // Try to find participant avatar
  if (props.participants && props.message.senderId) {
    const participant = props.participants.find(
      (p) => p.id === props.message.senderId || p.providerUserId === props.message.senderId
    );
    if (participant?.avatar || participant?.picture_url) {
      return participant.avatar || participant.picture_url;
    }
  }
  return null;
});

const avatarInitials = computed(() => {
  if (props.message.isOwn) return "Me";
  const name = props.message.senderName || "U";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

// Generate a consistent color from a name
const colorFromName = (name: string): string => {
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const avatarColor = computed(() => {
  if (props.message.isOwn) return "#3e3efe";
  return colorFromName(props.message.senderName || "U");
});

// The contact (non-self participant) whose read receipt is shown on our own messages
const contactParticipant = computed(() => {
  const readBy = props.message.readReceipts?.[0]?.readBy;
  if (readBy) {
    const reader = props.participants?.find((p) => p.providerUserId === readBy || p.id === readBy);
    if (reader) return reader;
  }
  return props.participants?.find((p) => !p.is_self) || null;
});

const contactAvatarUrl = computed(() => {
  const contact = contactParticipant.value;
  return contact?.avatar || contact?.picture_url || null;
});

const contactInitials = computed(() => {
  const name = contactParticipant.value?.name || "U";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

const contactColor = computed(() => {
  const name = contactParticipant.value?.name || "U";
  return colorFromName(name);
});

const statusInfo = computed(() => {
  if (!props.platform || !props.message.isOwn) {
    return { status: "sent" as const, icon: "", color: "", label: "" };
  }
  const platform = props.platform;
  if (platform === "whatsapp" || platform === "linkedin") {
    return getMessageStatus(props.message, platform);
  }
  return { status: "sent" as const, icon: "", color: "", label: "" };
});

const platformColors = computed(() => {
  if (!props.platform) return null;
  return getPlatformColors(props.platform);
});

const bubbleStyle = computed(() => {
  if (!platformColors.value) return {};
  if (props.message.isOwn) {
    return {
      backgroundColor: platformColors.value.outgoing,
      color: platformColors.value.outgoingText,
      maxWidth: getMaxMessageWidth(props.platform || "whatsapp"),
    };
  } else {
    return {
      backgroundColor: platformColors.value.incoming,
      color: platformColors.value.incomingText,
      maxWidth: getMaxMessageWidth(props.platform || "whatsapp"),
    };
  }
});

const formattedContent = computed(() => {
  if (!props.message.text) return "";
  if (!props.platform) return props.message.text;

  if (props.message.text.includes("<a href=") || props.message.text.includes('<a href="')) {
    return props.message.text;
  }

  return formatMessageContent(props.message.text, props.platform);
});

const isHtmlContent = computed(() => {
  return formattedContent.value !== props.message.text;
});

const hasEmptyContent = computed(() => {
  const hasText = props.message.text && props.message.text.trim().length > 0;
  const hasAttachments = props.message.attachments && props.message.attachments.length > 0;

  return !hasText && !hasAttachments && !props.message.isEvent && !props.message.deleted;
});

// Get message ID for attachment resolution
const messageId = computed(() => {
  return props.message.id || "";
});

// Helper: create attachment URL for lightbox
const resolveAttachmentUrl = (attachment: any): string | null => {
  if (!attachment.url || attachment.unavailable) return null;

  if (attachment.url.startsWith("att://") && messageId.value && attachment.id) {
    return `/unipile/messages/${messageId.value}/attachments/${attachment.id}`;
  }

  return attachment.url;
};

// Collect all image URLs from message for lightbox
const allImageUrls = computed(() => {
  const urls: string[] = [];

  // Images from main attachments
  if (props.message.attachments) {
    props.message.attachments
      .filter((att) => att.type === "img" && att.url && !att.unavailable)
      .forEach((att) => {
        const url = resolveAttachmentUrl(att);
        if (url) urls.push(url);
      });
  }

  // Images from quoted message attachments
  if (props.message.quoted?.attachments) {
    props.message.quoted.attachments
      .filter((att) => att.type === "img" && att.url && !att.unavailable)
      .forEach((att) => {
        const url = resolveAttachmentUrl(att);
        if (url) urls.push(url);
      });
  }

  return urls;
});

// Handle image click - open lightbox
const handleImageClick = (url: string, attachment: any) => {
  const imageUrls = allImageUrls.value;
  if (imageUrls.length === 0) return;

  let index = imageUrls.findIndex((u) => u === url);

  // If not found, try to match by attachment ID
  if (index < 0 && attachment.id) {
    index = imageUrls.findIndex((u) => u.includes(attachment.id));
  }

  lightboxCurrentIndex.value = index >= 0 ? index : 0;
  lightboxImages.value = imageUrls;
  showImageLightbox.value = true;
};

const closeImageLightbox = () => {
  showImageLightbox.value = false;
};

const formatChatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
  const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return `${dayName}, ${time}`;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getEventIcon = (eventType?: number): string => {
  switch (eventType) {
    case 1:
    case 2:
      return "mdi:emoticon-happy-outline";
    case 3:
      return "mdi:account-group";
    case 4:
      return "mdi:rename-box";
    case 5:
      return "mdi:account-plus";
    case 6:
      return "mdi:account-minus";
    case 7:
      return "mdi:account-arrow-left";
    case 8:
    case 9:
      return "mdi:phone-missed";
    default:
      return "mdi:information-outline";
  }
};

const getEventText = (eventType?: number): string => {
  switch (eventType) {
    case 1:
      return "A user reacted to a message";
    case 2:
      return "A user reacted to your message";
    case 3:
      return "The group was created";
    case 4:
      return "The group title was changed";
    case 5:
      return "A new participant was added to the group";
    case 6:
      return "A participant was removed from the group";
    case 7:
      return "A participant left the group";
    case 8:
      return "Missed voice call";
    case 9:
      return "Missed audio call";
    default:
      return "System event";
  }
};

const quotedSenderName = computed(() => {
  if (!props.message.quoted) return null;
  if (props.message.quoted.senderName) {
    return props.message.quoted.senderName;
  }
  if (props.message.quoted.sender_id && props.participants) {
    const participant = props.participants.find(
      (p) => p.providerUserId === props.message.quoted?.sender_id
    );
    if (participant?.name) {
      return participant.name;
    }
  }
  return props.message.quoted.sender_id || "Unknown";
});
</script>

<style scoped>
.message-item {
  margin-bottom: 0.4rem;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: messageSlideIn 0.3s ease-out;
}

.message-item__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.message-item--own .message-item__row {
  flex-direction: row-reverse;
}

.message-item__avatar {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  overflow: hidden;
}

.message-item__avatar--own {
  background: #3e3efe;
}

.message-item__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.message-item__avatar-initials {
  font-size: 0.625rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
  text-transform: uppercase;
}

.message-item__stack {
  display: inline-flex;
  flex-direction: column;
  align-self: flex-start;
  width: 100%;
}

.message-item--own.message-item--whatsapp .message-item__stack,
.message-item--own.message-item--linkedin .message-item__stack,
.message-item--own.message-item--sms .message-item__stack {
  align-self: flex-end;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-item__bubble {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.message-item:not(.message-item--whatsapp):not(.message-item--linkedin):not(
    .message-item--sms
  ):hover
  .message-item__bubble {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.message-item:not(.message-item--whatsapp):not(.message-item--linkedin):not(.message-item--sms) {
  padding: 1rem;
  border-radius: 8px;
  background: #f9fafb;
}

.message-item--own:not(.message-item--whatsapp):not(.message-item--linkedin):not(
    .message-item--sms
  ) {
  background: #f3f4f6;
}

.message-item--whatsapp {
  padding: 0;
  background: transparent;
}

.message-item--linkedin {
  padding: 0;
  background: transparent;
}

.message-item--sms {
  padding: 0;
  background: transparent;
}

.message-item__bubble {
  padding: 0.65rem 0.75rem;
  border-radius: 16px;
  position: relative;
  word-wrap: normal;
  overflow-wrap: break-word;
  display: inline-block;
  align-self: flex-start;
  max-width: 75%;
  box-shadow: none;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.message-item:hover .message-item__bubble {
  transform: none;
  box-shadow: none;
}

.message-item__bubble--outgoing {
  align-self: flex-end;
  margin-left: auto;
}

.message-item__bubble--incoming {
  align-self: flex-start;
  margin-right: auto;
}

.message-item__bubble--whatsapp {
  border-radius: 16px;
}

.message-item__bubble--whatsapp.message-item__bubble--outgoing {
  border-bottom-right-radius: 4px;
}

.message-item__bubble--whatsapp.message-item__bubble--incoming {
  border-bottom-left-radius: 4px;
  border: 1px solid #e5e7eb;
}

.message-item__bubble--sms {
  border-radius: 16px;
}

.message-item__bubble--sms.message-item__bubble--outgoing {
  border-bottom-right-radius: 4px;
}

.message-item__bubble--sms.message-item__bubble--incoming {
  border-bottom-left-radius: 4px;
}

.message-item__sender {
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.125rem;
  opacity: 0.9;
}

.message-item__meta {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  margin-bottom: 0.125rem;
}

.message-item__meta-name {
  font-weight: 500;
  font-size: 0.8125rem;
  line-height: 1.2;
}

.message-item__meta-sep {
  font-size: 0.8125rem;
  opacity: 0.65;
}

.message-item__meta-time {
  font-size: 0.6875rem;
  opacity: 0.7;
}

.message-item__timestamp {
  font-size: 0.6875rem;
  opacity: 0.7;
  margin-left: 0.5rem;
}

.message-item__status {
  display: inline-flex;
  align-items: center;
  margin-left: 0.25rem;
  font-size: 0.875rem;
  transition:
    color 0.3s ease,
    transform 0.2s ease;
}

.message-item__status:hover {
  transform: scale(1.1);
}

.message-item__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 0.25rem;
  gap: 0.25rem;
}

.message-item__bubble--incoming .message-item__footer {
  justify-content: flex-start;
}

.message-item__subject {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
}

.message-item__body {
  font-size: 0.875rem;
  line-height: 1.3;
  margin-bottom: 0.25rem;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.message-item--linkedin .message-item__body,
.message-item--sms .message-item__body {
  margin-bottom: 0;
}

.message-item__body--html {
  line-height: 1.4;
}

.message-item__body--html :deep(a) {
  color: inherit;
  text-decoration: underline;
  opacity: 0.8;
}

.message-item__body--html :deep(br) {
  line-height: 1.4;
}

.message-item__read-time {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

.message-item__read-avatar {
  width: 14px;
  height: 14px;
  min-width: 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.message-item__read-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.message-item__read-avatar-initials {
  font-size: 0.5rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1;
  text-transform: uppercase;
}

.message-item__read-time--inline {
  margin-top: 0;
  padding: 0;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.message-item--whatsapp .message-item__read-time--inline,
.message-item--linkedin .message-item__read-time--inline,
.message-item--sms .message-item__read-time--inline {
  display: inline-flex;
  margin-top: 4px;
  margin-right: 0;
  width: auto;
}

.message-item--own.message-item--whatsapp .message-item__read-time--inline,
.message-item--own.message-item--linkedin .message-item__read-time--inline,
.message-item--own.message-item--sms .message-item__read-time--inline {
  align-self: flex-end;
  margin-left: auto;
}

.message-item__actions {
  display: flex;
  gap: 0.5rem;
}

.message-item__actions--hover {
  opacity: 0;
  transition: opacity 0.2s;
  margin-top: 0.25rem;
}

.message-item:hover .message-item__actions--hover {
  opacity: 1;
}

.message-item--whatsapp .message-item__actions,
.message-item--linkedin .message-item__actions,
.message-item--sms .message-item__actions {
  display: inline-flex;
  gap: 6px;
  margin-top: 4px;
  padding: 2px 6px;
  border: none;
  border-radius: 999px;
  background: transparent;
  width: auto;
}

.message-item--own.message-item--whatsapp .message-item__actions,
.message-item--own.message-item--linkedin .message-item__actions,
.message-item--own.message-item--sms .message-item__actions {
  align-self: flex-end;
  margin-left: auto;
}

.message-item__action-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 999px;
}

.message-item__action-icon-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.message-item__action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.message-item__action-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.message-item--event {
  background: #f0f9ff;
  border-left: 3px solid #3b82f6;
}

.message-item--deleted {
  opacity: 0.6;
}

.message-item--pending {
  opacity: 0.7;
}

.message-item--pending .message-item__bubble {
  position: relative;
}

.message-item__event {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e40af;
  margin-bottom: 0.5rem;
}

.message-item__quoted {
  margin: 0.5rem 0 0.5rem 0;
  padding: 0.5rem 0.6rem;
  background: rgba(255, 255, 255, 0.3);
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
  position: relative;
}

.message-item__bubble--incoming .message-item__quoted {
  background: rgba(255, 255, 255, 0.5);
}

.message-item__bubble--outgoing .message-item__quoted {
  background: rgba(255, 255, 255, 0.25);
}

.message-item__quoted-text {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.4;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.message-item__bubble--outgoing .message-item__quoted-text {
  color: rgba(0, 0, 0, 0.7);
}

.message-item__body--deleted {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;
  font-style: italic;
}

.message-item__body--unsupported {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-style: italic;
  font-size: 0.8125rem;
  opacity: 0.8;
  padding: 0.5rem 0;
}

.message-item__reactions {
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
}

.message-item__reaction {
  display: flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border-radius: 12px;
  font-size: 0.875rem;
  transition:
    background 0.15s ease,
    transform 0.15s ease;
}

.message-item__reaction--own {
  cursor: pointer;
  background: #e0e7ff;
}

.message-item__reaction--own:hover {
  background: #c7d2fe;
  transform: scale(1.05);
}

.message-item__reaction-emoji {
  font-size: 1rem;
}

.message-item__edited {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #9ca3af;
  flex-wrap: wrap;
}

.message-item__show-original-btn {
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.6875rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}

.message-item__show-original-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.message-item__original-content {
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  font-size: 0.8125rem;
  color: #6b7280;
  font-style: italic;
}

.message-item__quoted-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.9;
}

.message-item__quoted-sender {
  font-weight: 600;
}

.message-item__quoted-attachments {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.message-item__attachments {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.message-item__type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #0077b5;
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.message-item__attendee-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
